import {createFileRoute} from '@tanstack/react-router'
import React from 'react'
import {OVERLAY_BOOT_SCRIPT, OVERLAY_STATIC_CSS} from '../overlay/overlayBoot'
import OverlayPage from '../overlay/OverlayPage'

type Param = string | number
type OverlaySearch = {position?: Param; scale?: Param; theme?: Param; hide_idle?: Param; preview?: Param; fixture?: Param; poll?: Param; debug?: Param}

// The OBS Browser Source page for a streaming overlay token (docs/stream-overlay/README.md).
// Public and chrome-less like /embed: outside the authed layout so there is no sidebar and no
// login redirect. Transparency and the storage guard are served in <head>, before any bundle,
// so a WebView that never runs the app still paints nothing rather than a white box.
export const Route = createFileRoute('/overlay/$token')({
    component: OverlayPage,
    errorComponent: OverlayError,
    validateSearch: (search: Record<string, unknown>): OverlaySearch => {
        // The router JSON-parses search values, so ?preview=1 arrives as the number 1. Keep numbers
        // as numbers: converting would make the router rewrite the address bar as preview=%221%22.
        const s = (v: unknown) => (typeof v === 'string' || typeof v === 'number' ? v : undefined)
        return {position: s(search.position), scale: s(search.scale), theme: s(search.theme), hide_idle: s(search.hide_idle), preview: s(search.preview), fixture: s(search.fixture), poll: s(search.poll), debug: s(search.debug)}
    },
    head: () => ({
        meta: [
            {title: 'ScorePal overlay'},
            {name: 'robots', content: 'noindex, nofollow'},
            // Real viewport, not a fixed 1920: phones and mobile streaming apps load this page too,
            // and the stylesheet sizes everything from the viewport width.
            {name: 'viewport', content: 'width=device-width, initial-scale=1'},
        ],
        styles: [{children: OVERLAY_STATIC_CSS}],
        scripts: [{children: OVERLAY_BOOT_SCRIPT}],
    }),
})

/**
 * If the page itself throws, stay transparent — an OBS scene must never show an error page — and
 * only with ?debug=1 print what went wrong, top-left, for reading on a phone.
 */
function OverlayError({error}: {error: Error}) {
    const debug = typeof window !== 'undefined' && /[?&]debug=1(&|$)/.test(window.location.search)
    if (!debug) return null
    return (
        <pre style={{position: 'absolute', top: 4, left: 4, margin: 0, padding: '4px 6px', maxWidth: '92vw', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                     background: 'rgba(0,0,0,.7)', color: '#f66', font: '11px/1.35 monospace', borderRadius: 4}}>
            {`overlay crashed: ${error?.message || String(error)}\n${(error?.stack || '').split('\n').slice(0, 4).join('\n')}`}
        </pre>
    )
}
